import React, { useEffect, useRef, useState } from 'react';
import CustomSelect from '../components/CustomSelect';

export default function RosterView({
  roster,
  searchTerm,
  isLoading,
  isSaving,
  saveError,
  onSaveStudent,
  onCreateStudent,
  onDeleteStudent,
  onImportRoster,
  onUpdateStudentRelationships,
  terminology,
  permissions
}) {
  const canWrite = (permissions ?? []).includes('students.write');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [flashStudentId, setFlashStudentId] = useState(null);
  const flashTimeoutRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState('append');
  const [importFile, setImportFile] = useState(null);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [importReport, setImportReport] = useState(null);
  const [relationshipDialog, setRelationshipDialog] = useState({
    type: '',
    selectedIds: []
  });
  const [isEditorDialogOpen, setIsEditorDialogOpen] = useState(false);
  const [relationshipSearchTerm, setRelationshipSearchTerm] = useState('');

  const filteredStudents = roster
    ? roster.students.filter((student) => {
        const searchValue = searchTerm.trim().toLowerCase();
        if (!searchValue) {
          return true;
        }

        return (
          student.displayName.toLowerCase().includes(searchValue) ||
          student.externalStudentId.toLowerCase().includes(searchValue) ||
          student.gradeLevel.toLowerCase().includes(searchValue) ||
          (student.assignedClassName || '').toLowerCase().includes(searchValue)
        );
      })
    : [];

  useEffect(() => {
    if (!roster || roster.students.length === 0) {
      setSelectedStudentId('');
      setDraft(null);
      setIsEditorDialogOpen(false);
      setRelationshipDialog({ type: '', selectedIds: [] });
      setRelationshipSearchTerm('');
      return;
    }

    if (selectedStudentId) {
      const currentSelection = roster.students.find((student) => student.id === selectedStudentId);
      if (!currentSelection) {
        setSelectedStudentId('');
        setDraft(null);
        setIsEditorDialogOpen(false);
      }
    }
  }, [roster, selectedStudentId]);

  useEffect(() => {
    if (!roster || !selectedStudentId) {
      setDraft(null);
      return;
    }

    const selectedStudent = roster.students.find((student) => student.id === selectedStudentId);
    setDraft(selectedStudent ? createDraft(selectedStudent) : null);
  }, [roster, selectedStudentId]);

  if (isLoading || !roster) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading {terminology.students.toLowerCase()}</h1>
          <p>Preparing the editable {terminology.student.toLowerCase()} workspace.</p>
        </div>
      </section>
    );
  }

  const selectedStudent = roster.students.find((student) => student.id === selectedStudentId) ?? null;

  function handleScrollToStudent(studentId) {
    const row = document.getElementById(`roster-row-${studentId}`);
    if (!row) return;

    row.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
    }
    setFlashStudentId(studentId);
    flashTimeoutRef.current = setTimeout(() => {
      setFlashStudentId(null);
      flashTimeoutRef.current = null;
    }, 1800);
  }

  const activeRelationshipType = relationshipDialog.type;
  const isRelationshipDialogOpen = Boolean(activeRelationshipType);
  const relationshipCandidates = selectedStudent
    ? roster.students.filter(
        (student) =>
          student.id !== selectedStudent.id &&
          student.gradeLevel === selectedStudent.gradeLevel
      )
    : [];
  const conflictingRelationshipIds = new Set(
    activeRelationshipType === 'friend'
      ? selectedStudent?.relatedStudentIds?.keepAway ?? []
      : activeRelationshipType === 'keepAway'
        ? selectedStudent?.relatedStudentIds?.friend ?? []
        : []
  );
  const filteredRelationshipCandidates = relationshipCandidates.filter((student) => {
    if (conflictingRelationshipIds.has(student.id)) {
      return false;
    }

    const searchValue = relationshipSearchTerm.trim().toLowerCase();
    if (!searchValue) {
      return true;
    }

    return (
      student.displayName.toLowerCase().includes(searchValue) ||
      student.externalStudentId.toLowerCase().includes(searchValue)
    );
  });
  const fixedClassOptions = draft
    ? roster.classes.filter((schoolClass) => schoolClass.gradeLevel === draft.gradeLevel)
    : [];
  const rosterRows = [];
  let currentGradeLevel = '';

  for (const student of filteredStudents) {
    if (student.gradeLevel !== currentGradeLevel) {
      currentGradeLevel = student.gradeLevel;
      rosterRows.push({
        kind: 'grade-divider',
        id: `grade-${currentGradeLevel}`,
        gradeLevel: currentGradeLevel
      });
    }

    rosterRows.push({
      kind: 'student',
      id: student.id,
      student
    });
  }

  function handleRowClick(studentId) {
    setSelectedStudentId(studentId);
    setIsEditorDialogOpen(true);
  }

  function handleCloseEditorDialog() {
    if (isSaving) return;
    setIsEditorDialogOpen(false);
  }

  async function handleSave(event) {
    event.preventDefault();

    if (!selectedStudent || !draft) {
      return;
    }

    const result = await onSaveStudent(selectedStudent.id, {
      ...draft,
      academicScore: Number(draft.academicScore)
    });

    if (result) {
      setIsEditorDialogOpen(false);
    }
  }

  function handleStartCreate() {
    setIsCreatingNew(true);
    setSelectedStudentId('');
    setDraft({
      displayName: '',
      externalStudentId: '',
      gradeLevel: roster?.grades?.[0]?.name || '',
      gender: '',
      academicScore: 0,
      behaviorFlag: false,
      fixedClassId: '',
      fixedAssignmentReason: '',
      notes: '',
      isActive: true,
      customFactors: {}
    });
  }

  function handleCancelCreate() {
    setIsCreatingNew(false);
    if (roster?.students?.length > 0) {
      setSelectedStudentId(roster.students[0].id);
    }
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();
    if (!draft) return;

    const result = await onCreateStudent({
      ...draft,
      academicScore: Number(draft.academicScore)
    });

    if (result) {
      setIsCreatingNew(false);
    }
  }

  async function handleDelete() {
    if (!selectedStudent || isSaving) return;
    if (!window.confirm(`Permanently delete ${selectedStudent.displayName}? This cannot be undone.`)) return;

    const result = await onDeleteStudent(selectedStudent.id);
    if (result) {
      setSelectedStudentId('');
      setIsEditorDialogOpen(false);
    }
  }

  async function handleImportSubmit(event) {
    event.preventDefault();
    if (!importFile) return;

    setImportError('');
    setImportSuccess('');
    setImportReport(null);

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      const result = await onImportRoster(base64, importFile.name, importMode);

      if (result) {
        const summary = result.importSummary;
        const msg = summary
          ? `Imported ${summary.imported} record${summary.imported === 1 ? '' : 's'}.${summary.errors?.length ? ` ${summary.errors.length} row(s) skipped.` : ''}`
          : 'Import complete.';
        setImportSuccess(msg);
        setImportFile(null);
        if (summary?.errors?.length) {
          setImportError(summary.errors.join('\n'));
        }
        if (summary?.rows) {
          setImportReport(summary);
        }
      } else {
        setImportError(saveError || 'Import failed.');
      }
    };
    reader.readAsDataURL(importFile);
  }

  function handleOpenImportDialog() {
    setIsImportDialogOpen(true);
    setImportFile(null);
    setImportMode('append');
    setImportError('');
    setImportSuccess('');
    setImportReport(null);
  }

  function handleCloseImportDialog() {
    if (isSaving) return;
    setIsImportDialogOpen(false);
  }

  function handleDownloadSampleCsv() {
    const headers = terminology.showAcademicScore
      ? 'last_name,first_name,student_id,grade,gender,academic_score,behavior_flag,notes,friend_requests'
      : 'last_name,first_name,student_id,grade,gender,behavior_flag,notes,friend_requests';
    const row1 = terminology.showAcademicScore
      ? 'Smith,Jane,S-001,,,85,false,,"Tom Lee"'
      : 'Smith,Jane,S-001,,,false,,"Tom Lee"';
    const row2 = terminology.showAcademicScore
      ? 'Lee,Tom,S-002,,,72,true,"Needs close supervision","Jane Smith"'
      : 'Lee,Tom,S-002,,,true,"Needs close supervision","Jane Smith"';
    const csv = [headers, row1, row2].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'sample-roster.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function handleDraftChange(field, value) {
    setDraft((currentDraft) => {
      const nextDraft = {
        ...currentDraft,
        [field]: value
      };

      if (field === 'gradeLevel') {
        const hasMatchingFixedClass = roster.classes.some(
          (schoolClass) =>
            schoolClass.id === currentDraft.fixedClassId && schoolClass.gradeLevel === value
        );

        if (!hasMatchingFixedClass) {
          nextDraft.fixedClassId = '';
          nextDraft.fixedAssignmentReason = '';
        }
      }

      if (field === 'fixedClassId' && !value) {
        nextDraft.fixedAssignmentReason = '';
      }

      return nextDraft;
    });
  }

  function handleCustomFactorChange(key, value) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      customFactors: {
        ...currentDraft.customFactors,
        [key]: value
      }
    }));
  }

  function handleReset() {
    if (!selectedStudent) {
      return;
    }

    setDraft(createDraft(selectedStudent));
  }

  function openRelationshipDialog(type) {
    if (!selectedStudent || isSaving) {
      return;
    }

    setRelationshipDialog({
      type,
      selectedIds: [...(selectedStudent.relatedStudentIds?.[type] ?? [])]
    });
    setRelationshipSearchTerm('');
  }

  function closeRelationshipDialog() {
    if (isSaving) {
      return;
    }

    setRelationshipDialog({
      type: '',
      selectedIds: []
    });
    setRelationshipSearchTerm('');
  }

  function toggleRelationshipCandidate(studentId) {
    setRelationshipDialog((currentDialog) => ({
      ...currentDialog,
      selectedIds: currentDialog.selectedIds.includes(studentId)
        ? currentDialog.selectedIds.filter((candidateId) => candidateId !== studentId)
        : [...currentDialog.selectedIds, studentId]
    }));
  }

  async function handleRelationshipSubmit(event) {
    event.preventDefault();

    if (!selectedStudent || !activeRelationshipType) {
      return;
    }

    const updatedRoster = await onUpdateStudentRelationships(
      selectedStudent.id,
      activeRelationshipType === 'keepAway' ? 'keep_away' : activeRelationshipType,
      relationshipDialog.selectedIds
    );

    if (updatedRoster) {
      closeRelationshipDialog();
    }
  }

  return (
    <>
      <section className="hero-card roster-hero">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill accent">{terminology.students}</span>
            <span className="eyebrow-copy">
              {roster.summary.activeStudents} active {terminology.students.toLowerCase()}
            </span>
          </div>
          <h1>{roster.project.name}</h1>
          <p>
            Update {terminology.student.toLowerCase()} attributes, lock placements, and control which records are active before
            you generate the next proposal.
          </p>
        </div>

        <div className="hero-actions">
          <div className="roster-stat-block">
            <span className="metric-label">{terminology.students}</span>
            <strong>{roster.summary.totalStudents}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Active</span>
            <strong>{roster.summary.activeStudents}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Locked</span>
            <strong>{roster.summary.lockedStudents}</strong>
          </div>
        </div>
        {canWrite ? (
          <div className="roster-action-bar">
            <button type="button" className="utility-button compact-button" onClick={handleOpenImportDialog} disabled={isSaving}>
              Import CSV
            </button>
            <button type="button" className="primary-button compact-button" onClick={handleStartCreate} disabled={isSaving}>
              + Add {terminology.student.toLowerCase()}
            </button>
          </div>
        ) : null}
      </section>

      <section className="panel" style={{ marginTop: 'var(--gap)' }}>
        <div className="panel-head">
          <div>
            <span className="panel-kicker">Editable {terminology.students.toLowerCase()}</span>
            <h2>{terminology.student} records</h2>
          </div>
          <span className="toolbar-meta">{filteredStudents.length} visible {terminology.students.toLowerCase()}</span>
        </div>

        <div className="table-wrap roster-list-wrap">
          <table className="data-grid roster-grid-table">
                <thead>
                  <tr>
                    <th>{terminology.student}</th>
                    <th>Grade</th>
                    <th>Status</th>
                    <th>Assigned</th>
                    <th>Lock</th>
                    <th>Friends</th>
                    <th>Keep Away</th>
                    <th>Custom Factors</th>
                  </tr>
                </thead>
                <tbody>
                  {rosterRows.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--muted)' }}>
                        No {terminology.students.toLowerCase()} to show
                      </td>
                    </tr>
                  )}
                  {rosterRows.map((row) => {
                    if (row.kind === 'grade-divider') {
                      return (
                        <tr key={row.id} className="roster-grade-divider">
                          <td colSpan="8">Grade {row.gradeLevel}</td>
                        </tr>
                      );
                    }

                    const { student } = row;

                    return (
                      <tr
                        key={student.id}
                        id={`roster-row-${student.id}`}
                        className={`${student.id === selectedStudentId ? 'is-selected' : ''} ${flashStudentId === student.id ? 'flash-row' : ''}`}
                        onClick={() => handleRowClick(student.id)}
                      >
                        <td>
                          <div className="student-cell">
                            <strong>{student.displayName}</strong>
                            <span>
                              {student.externalStudentId} • {student.gender || '-'}
                            </span>
                          </div>
                        </td>
                        <td>{student.gradeLevel}</td>
                        <td>
                          <span className={`tone-pill ${student.statusTone}`}>{student.status}</span>
                        </td>
                        <td className="suggested-class">{student.assignedClassName || 'Unassigned'}</td>
                        <td>{student.lockedClass || 'Open'}</td>
                        <td>
                          <div className="relationship-chip-row">
                            {(student.relatedStudentNames?.friend ?? []).length === 0
                              ? null
                              : (student.relatedStudentNames.friend).map((name, idx) => {
                                  const relatedId = student.relatedStudentIds?.friend?.[idx];
                                  return (
                                    <span
                                      key={relatedId ?? name}
                                      className={`relationship-chip relationship-chip-clickable`}
                                      onClick={(e) => { e.stopPropagation(); if (relatedId) handleScrollToStudent(relatedId); }}
                                    >
                                      {name}
                                    </span>
                                  );
                                })
                            }
                          </div>
                        </td>
                        <td>
                          <div className="relationship-chip-row">
                            {(student.relatedStudentNames?.keepAway ?? []).length === 0
                              ? null
                              : (student.relatedStudentNames.keepAway).map((name, idx) => {
                                  const relatedId = student.relatedStudentIds?.keepAway?.[idx];
                                  return (
                                    <span
                                      key={relatedId ?? name}
                                      className={`relationship-chip relationship-chip-keep-away relationship-chip-clickable`}
                                      onClick={(e) => { e.stopPropagation(); if (relatedId) handleScrollToStudent(relatedId); }}
                                    >
                                      {name}
                                    </span>
                                  );
                                })
                            }
                          </div>
                        </td>
                        <td>
                          <div className="signal-list">
                            {student.signals.slice(0, 3).map((signal) => (
                              <span key={signal} className="signal-pill">
                                {signal}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
          </table>
        </div>
      </section>

      {isCreatingNew && draft ? (
        <div className="workspace-dialog-backdrop" onClick={handleCancelCreate}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-student-title"
            style={{ width: 'min(640px, calc(100vw - 48px))', maxHeight: 'min(90vh, 800px)', overflowY: 'auto' }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => { if (event.key === 'Escape') handleCancelCreate(); }}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">New {terminology.student.toLowerCase()}</span>
                <h2 id="create-student-title">Add {terminology.student.toLowerCase()}</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCancelCreate}
                aria-label="Close create dialog"
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleCreateSubmit}>
              <label className="auth-field">
                <span>{terminology.student} name</span>
                <input
                  type="text"
                  value={draft.displayName}
                  onChange={(event) => handleDraftChange('displayName', event.target.value)}
                  placeholder="Full name"
                  required
                  autoFocus
                />
              </label>

              <div className="roster-form-grid">
                <label className="auth-field">
                  <span>{terminology.student} ID</span>
                  <input
                    type="text"
                    value={draft.externalStudentId}
                    onChange={(event) => handleDraftChange('externalStudentId', event.target.value)}
                    placeholder="Optional"
                  />
                </label>

                <label className="auth-field">
                  <span>Grade level</span>
                  <CustomSelect
                    value={draft.gradeLevel}
                    options={(roster.grades ?? []).map((grade) => ({ value: grade.name, label: grade.name }))}
                    onChange={(val) => handleDraftChange('gradeLevel', val)}
                  />
                </label>

                <label className="auth-field">
                  <span>Gender</span>
                  <CustomSelect
                    value={draft.gender}
                    options={[{ value: '', label: 'Unspecified' }, { value: 'F', label: 'F' }, { value: 'M', label: 'M' }, { value: 'X', label: 'X' }]}
                    onChange={(val) => handleDraftChange('gender', val)}
                  />
                </label>

                {terminology.showAcademicScore ? (
                  <label className="auth-field">
                    <span>Academic score</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={draft.academicScore}
                      onChange={(event) => handleDraftChange('academicScore', event.target.value)}
                    />
                  </label>
                ) : null}
              </div>

              <label className="auth-field">
                <span>Notes</span>
                <textarea
                  value={draft.notes}
                  onChange={(event) => handleDraftChange('notes', event.target.value)}
                  rows="3"
                  placeholder="Optional notes"
                />
              </label>

              <div className="roster-form-actions">
                <button type="button" className="utility-button" onClick={handleCancelCreate} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || !canWrite}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Creating' : `Create ${terminology.student.toLowerCase()}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isEditorDialogOpen && draft && selectedStudent ? (
        <div className="workspace-dialog-backdrop" onClick={handleCloseEditorDialog}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-student-title"
            style={{ width: 'min(640px, calc(100vw - 48px))', maxHeight: 'min(90vh, 800px)', overflowY: 'auto' }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => { if (event.key === 'Escape') handleCloseEditorDialog(); }}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Record editor</span>
                <h2 id="edit-student-title">{selectedStudent.displayName}</h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className={`tone-pill ${selectedStudent.statusTone}`}>{selectedStudent.status}</span>
                <button
                  type="button"
                  className="icon-button classes-dialog-close"
                  onClick={handleCloseEditorDialog}
                  aria-label="Close editor dialog"
                  disabled={isSaving}
                >
                  ×
                </button>
              </div>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form roster-editor-compact" onSubmit={handleSave}>
              {/* Row 1: Name + ID */}
              <div className="roster-editor-row-2">
                <label className="auth-field">
                  <span>{terminology.student} name</span>
                  <input
                    type="text"
                    value={draft.displayName}
                    onChange={(event) => handleDraftChange('displayName', event.target.value)}
                  />
                </label>
                <label className="auth-field">
                  <span>{terminology.student} ID</span>
                  <input
                    type="text"
                    value={draft.externalStudentId}
                    onChange={(event) => handleDraftChange('externalStudentId', event.target.value)}
                  />
                </label>
              </div>

              {/* Row 2: Grade + Gender + Fixed class */}
              <div className="roster-editor-row-3">
                <div className="auth-field">
                  <span>Grade</span>
                  <CustomSelect
                    value={draft.gradeLevel}
                    options={buildGradeOptions(roster.grades, draft.gradeLevel).map((grade) => ({
                      value: grade.name,
                      label: grade.name
                    }))}
                    onChange={(val) => handleDraftChange('gradeLevel', val)}
                  />
                </div>
                <div className="auth-field">
                  <span>Gender</span>
                  <CustomSelect
                    value={draft.gender}
                    options={[
                      { value: '', label: '—' },
                      { value: 'F', label: 'F' },
                      { value: 'M', label: 'M' },
                      { value: 'X', label: 'X' }
                    ]}
                    onChange={(val) => handleDraftChange('gender', val)}
                  />
                </div>
              </div>

              {terminology.showAcademicScore ? (
                <label className="auth-field">
                  <span>Academic score</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={draft.academicScore}
                    onChange={(event) => handleDraftChange('academicScore', event.target.value)}
                  />
                </label>
              ) : null}

              {/* Row 3: toggles */}
              <div className="roster-editor-row-lock">

                <div className="roster-toggle-row" style={{ paddingTop: '22px' }}>
                  <label className="roster-checkbox">
                    <input
                      type="checkbox"
                      checked={draft.behaviorFlag}
                      onChange={(event) => handleDraftChange('behaviorFlag', event.target.checked)}
                    />
                    <span>Behavior</span>
                  </label>
                  <label className="roster-checkbox">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(event) => handleDraftChange('isActive', event.target.checked)}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              {roster.customFactorOptions.length > 0 ? (
              <div className="roster-custom-factors">
                <span className="panel-kicker">Custom factors</span>
                <div className="roster-signal-grid">
                  {roster.customFactorOptions.map((factor) => {
                    if (factor.dataType === 'enum') {
                      return (
                        <label key={factor.key} className="auth-field roster-factor-field">
                          <span>{factor.label}</span>
                          <CustomSelect
                            value={draft.customFactors[factor.key] ?? ''}
                            options={[{ value: '', label: 'Not set' }, ...(factor.options?.choices ?? []).map((choice) => ({ value: choice, label: choice }))]}
                            onChange={(val) => handleCustomFactorChange(factor.key, val || null)}
                          />
                        </label>
                      );
                    }

                    if (factor.dataType === 'number') {
                      const numMin = factor.options?.min;
                      const numMax = factor.options?.max;
                      return (
                        <label key={factor.key} className="auth-field roster-factor-field">
                          <span>{factor.label}{numMin != null ? ` (${numMin}–${numMax})` : ''}</span>
                          <input
                            type="number"
                            value={draft.customFactors[factor.key] ?? ''}
                            onChange={(e) => {
                              if (e.target.value === '') {
                                handleCustomFactorChange(factor.key, null);
                                return;
                              }
                              const n = Number(e.target.value);
                              if (!Number.isFinite(n)) return;
                              if (numMin != null && n < numMin) return;
                              if (numMax != null && n > numMax) return;
                              handleCustomFactorChange(factor.key, n);
                            }}
                            min={numMin}
                            max={numMax}
                            step="1"
                            placeholder={numMin != null ? `${numMin}–${numMax}` : ''}
                          />
                        </label>
                      );
                    }

                    return (
                      <label key={factor.key} className="roster-checkbox signal-checkbox">
                        <input
                          type="checkbox"
                          checked={draft.customFactors[factor.key] === true}
                          onChange={() => handleCustomFactorChange(factor.key, !draft.customFactors[factor.key])}
                        />
                        <span>{factor.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              ) : null}

              <div className="roster-related-grid">
                <div className="mini-panel">
                  <div className="roster-related-card-head">
                    <span className="mini-label">Friend requests</span>
                    <button
                      type="button"
                      className="utility-button compact-button"
                      onClick={() => openRelationshipDialog('friend')}
                      disabled={isSaving || !canWrite}
                    >
                      Edit
                    </button>
                  </div>
                  <strong>{selectedStudent.friendRequestCount}</strong>
                  <p>{selectedStudent.relatedStudentNames.friend.join(', ') || 'None recorded'}</p>
                </div>
                <div className="mini-panel is-negative-panel">
                  <div className="roster-related-card-head">
                    <span className="mini-label">Keep away</span>
                    <button
                      type="button"
                      className="utility-button compact-button"
                      onClick={() => openRelationshipDialog('keepAway')}
                      disabled={isSaving || !canWrite}
                    >
                      Edit
                    </button>
                  </div>
                  <strong>{selectedStudent.keepAwayCount}</strong>
                  <p>{selectedStudent.relatedStudentNames.keepAway.join(', ') || 'None recorded'}</p>
                </div>
              </div>

              <label className="auth-field">
                <span>Notes</span>
                <textarea
                  value={draft.notes}
                  onChange={(event) => handleDraftChange('notes', event.target.value)}
                  rows="2"
                />
              </label>

              <div className="roster-form-actions">
                {canWrite ? (
                  <button type="button" className="utility-button danger-text" onClick={handleDelete} disabled={isSaving}>
                    Delete
                  </button>
                ) : null}
                <button type="button" className="utility-button" onClick={handleReset} disabled={isSaving}>
                  Reset
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || !canWrite}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Saving changes' : `Save ${terminology.student.toLowerCase()}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isRelationshipDialogOpen && selectedStudent ? (
        <div className="workspace-dialog-backdrop" onClick={closeRelationshipDialog}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-relationship-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">
                  {activeRelationshipType === 'friend' ? 'Friend requests' : 'Keep away'}
                </span>
                <h2 id="edit-relationship-title">
                  {selectedStudent.displayName}
                </h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={closeRelationshipDialog}
                aria-label="Close relationship dialog"
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleRelationshipSubmit}>
              <p className="editor-note">
                Choose from other {terminology.students.toLowerCase()} in Grade {selectedStudent.gradeLevel}.
              </p>
              <p className="editor-note">
                {activeRelationshipType === 'friend'
                  ? `${terminology.students} already listed under Keep away are hidden here.`
                  : `${terminology.students} already listed under Friend requests are hidden here.`}
              </p>

              {relationshipDialog.selectedIds.length > 0 ? (
                <div className="relationship-selected-chips">
                  {relationshipDialog.selectedIds.map((id) => {
                    const student = relationshipCandidates.find((s) => s.id === id);
                    if (!student) return null;
                    return (
                      <span
                        key={id}
                        className={`relationship-selected-chip ${activeRelationshipType === 'friend' ? 'relationship-chip-good' : 'relationship-chip-bad'}`}
                      >
                        {student.displayName}
                        <button
                          type="button"
                          className="relationship-chip-remove"
                          onClick={() => toggleRelationshipCandidate(id)}
                          aria-label={`Remove ${student.displayName}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : null}

              <div className="relationship-autocomplete">
                <label className="auth-field">
                  <span>Add {terminology.student.toLowerCase()}</span>
                  <input
                    type="search"
                    value={relationshipSearchTerm}
                    onChange={(event) => setRelationshipSearchTerm(event.target.value)}
                    placeholder="Search by name or ID"
                    autoFocus
                  />
                </label>

                <div className="relationship-option-list">
                  {relationshipCandidates.length === 0 ? (
                    <div className="loading-card relationship-empty-state">
                      <h1>No matching {terminology.students.toLowerCase()}</h1>
                      <p>There are no other {terminology.students.toLowerCase()} in this grade yet.</p>
                    </div>
                  ) : filteredRelationshipCandidates.filter((s) => !relationshipDialog.selectedIds.includes(s.id)).length === 0 ? (
                    <div className="loading-card relationship-empty-state">
                      <h1>{relationshipSearchTerm.trim() ? 'No results' : 'All added'}</h1>
                      <p>
                        {relationshipSearchTerm.trim()
                          ? `No ${terminology.students.toLowerCase()} in this grade match the current search.`
                          : `All available ${terminology.students.toLowerCase()} have been added.`}
                      </p>
                    </div>
                  ) : (
                    filteredRelationshipCandidates
                      .filter((s) => !relationshipDialog.selectedIds.includes(s.id))
                      .map((student) => (
                        <button
                          key={student.id}
                          type="button"
                          className="relationship-autocomplete-option"
                          onClick={() => toggleRelationshipCandidate(student.id)}
                        >
                          {student.displayName} ({student.externalStudentId})
                        </button>
                      ))
                  )}
                </div>
              </div>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={closeRelationshipDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || !canWrite}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Saving requests' : 'Save requests'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isImportDialogOpen ? (
        <div className="workspace-dialog-backdrop" onClick={handleCloseImportDialog}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-roster-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Import {terminology.students.toLowerCase()}</span>
                <h2 id="import-roster-title">Import from file</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseImportDialog}
                aria-label="Close import dialog"
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            {importSuccess ? <div className="auth-success">{importSuccess}</div> : null}
            {importError ? <div className="auth-error" style={{ whiteSpace: 'pre-wrap' }}>{importError}</div> : null}

            {importReport ? (
              <div className="import-report">
                {importReport.friendRequestSummary && (importReport.friendRequestSummary.matched > 0 || importReport.friendRequestSummary.unmatched > 0) ? (
                  <div className="import-report-summary">
                    Friend requests: {importReport.friendRequestSummary.matched} matched, {importReport.friendRequestSummary.unmatched} unmatched
                  </div>
                ) : null}

                <div className="import-report-table-wrap">
                  <table className="import-report-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Friend requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importReport.rows.map((row) => (
                        <tr key={row.row} className={row.status === 'skipped' ? 'import-row-skipped' : ''}>
                          <td>{row.row}</td>
                          <td>{row.name || '(empty)'}</td>
                          <td>
                            {row.status === 'imported' ? (
                              <span className="import-status-ok">Imported</span>
                            ) : (
                              <span className="import-status-error" title={row.error}>Skipped</span>
                            )}
                          </td>
                          <td>
                            {row.friendRequests.length === 0 ? (
                              <span className="text-muted">&mdash;</span>
                            ) : (
                              <ul className="import-friend-list">
                                {row.friendRequests.map((fr, idx) => (
                                  <li key={idx} className={`import-friend-${fr.status === 'matched' ? fr.matchType : 'none'}`}>
                                    {fr.status === 'matched' ? (
                                      <>
                                        {fr.matchType === 'exact' ? (
                                          <span className="import-match-exact" title="Exact match">{fr.requestedName}</span>
                                        ) : (
                                          <span className="import-match-fuzzy" title={`Fuzzy matched to "${fr.matchedName}"`}>
                                            {fr.requestedName} &rarr; {fr.matchedName}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="import-match-none" title="No match found">{fr.requestedName}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="roster-form-actions">
                  <button type="button" className="utility-button" onClick={handleCloseImportDialog}>
                    Close
                  </button>
                </div>
              </div>
            ) : (
            <form className="roster-form" onSubmit={handleImportSubmit}>
              <div className="editor-note">
                <p>Upload a CSV or Excel file (.xlsx, .xls) with {terminology.student.toLowerCase()} records.</p>
                <p><strong>Expected columns:</strong> last_name (required), first_name (required), student_id, grade, gender{terminology.showAcademicScore ? ', academic_score' : ''}, behavior_flag, notes, friend_requests</p>
              </div>

              <button type="button" className="utility-button compact-button" onClick={handleDownloadSampleCsv}>
                Download sample CSV
              </button>

              <label className="file-drop-zone">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                  className="file-drop-input"
                />
                {importFile ? (
                  <span className="file-drop-selected">
                    <span className="file-drop-icon">&#128196;</span>
                    <span className="file-drop-name">{importFile.name}</span>
                    <span className="file-drop-hint">Click to change file</span>
                  </span>
                ) : (
                  <span className="file-drop-placeholder">
                    <span className="file-drop-icon">&#128449;</span>
                    <span className="file-drop-cta">Click to choose a file</span>
                    <span className="file-drop-hint">CSV or Excel (.xlsx, .xls)</span>
                  </span>
                )}
              </label>

              <div className="roster-toggle-row">
                <label className="roster-checkbox">
                  <input
                    type="radio"
                    name="importMode"
                    checked={importMode === 'append'}
                    onChange={() => setImportMode('append')}
                  />
                  <span>Add to existing records</span>
                </label>
                <label className="roster-checkbox">
                  <input
                    type="radio"
                    name="importMode"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                  />
                  <span>Replace all existing records</span>
                </label>
              </div>

              {importMode === 'replace' ? (
                <div className="auth-error">
                  Warning: this will permanently delete all existing {terminology.student.toLowerCase()} records and relationships in this project.
                </div>
              ) : null}

              <div className="roster-form-actions">
                <button type="button" className="utility-button" onClick={handleCloseImportDialog} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || !importFile}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Importing' : `Import ${terminology.students.toLowerCase()}`}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

function createDraft(student) {
  return {
    externalStudentId: student.externalStudentId,
    displayName: student.displayName,
    gradeLevel: student.gradeLevel,
    gender: student.gender,
    academicScore: student.academicScore,
    behaviorFlag: student.behaviorFlag,
    fixedClassId: student.fixedClassId ?? '',
    fixedAssignmentReason: student.fixedAssignmentReason ?? '',
    notes: student.notes ?? '',
    isActive: student.isActive,
    customFactors: { ...(student.customFactors ?? {}) }
  };
}

function buildGradeOptions(grades, selectedGradeLevel) {
  const options = [...(grades ?? [])];

  if (selectedGradeLevel && !options.some((grade) => grade.name === selectedGradeLevel)) {
    options.push({
      id: selectedGradeLevel,
      name: selectedGradeLevel,
      sortOrder: Number.MAX_SAFE_INTEGER
    });
  }

  return options.sort(
    (left, right) =>
      left.sortOrder - right.sortOrder ||
      left.name.localeCompare(right.name, undefined, { numeric: true })
  );
}
